import api from '../lib/api';

export type TimeWindow = 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'custom';
export type AggregationLevel = 'agent' | 'team' | 'channel' | 'overall';

export interface MetricValue {
  current: number;
  previous: number;
  delta: number; // Percentage change
  trend: 'up' | 'down' | 'neutral';
  unit: string; // %, hours, count, $, etc.
}

export interface WFMAdherenceMetric extends MetricValue {
  scheduledHours: number;
  actualHours: number;
  adherenceRate: number;
}

export interface OccupancyMetric extends MetricValue {
  totalHours: number;
  productiveHours: number;
  occupancyRate: number;
}

export interface ProductivityMetric extends MetricValue {
  totalSolves: number;
  totalHours: number;
  solvesPerHour: number;
}

export interface CSATMetric extends MetricValue {
  totalResponses: number;
  satisfiedCount: number;
  averageScore: number;
}

export interface SLAMetric extends MetricValue {
  totalContacts: number;
  metSLA: number;
  complianceRate: number;
  byHour?: Record<number, number>;
  byDay?: Record<string, number>;
  byChannel?: Record<string, number>;
}

export interface CostPerContactMetric extends MetricValue {
  totalCost: number;
  totalContacts: number;
  costPerContact: number;
}

export interface QARatingMetric extends MetricValue {
  totalReviews: number;
  averageScore: number;
  passRate: number;
}

export interface PerformanceScorecard {
  aggregationLevel: AggregationLevel;
  entityId?: string; // agent/team/channel ID if not overall
  entityName?: string;
  timeWindow: {
    start: string;
    end: string;
    label: string;
  };
  metrics: {
    wfmAdherence: WFMAdherenceMetric;
    occupancy: OccupancyMetric;
    productivity: ProductivityMetric;
    csat: CSATMetric;
    sla: SLAMetric;
    costPerContact: CostPerContactMetric;
    qaRating: QARatingMetric;
  };
}

export interface AgentPerformanceRating {
  id: string;
  agentId: string;
  agentName: string;
  teamId?: string;
  teamName?: string;
  overallScore: number;
  rank: number;
  metricScores: {
    wfmAdherence: number;
    occupancy: number;
    productivity: number;
    csat: number;
    sla: number;
    costPerContact: number;
    qaRating: number;
  };
  metricWeights: {
    wfmAdherence: number;
    occupancy: number;
    productivity: number;
    csat: number;
    qaRating: number;
  };
}

export interface MetricDrilldown {
  metricName: string;
  timeWindow: {
    start: string;
    end: string;
  };
  aggregationLevel: AggregationLevel;
  entityId?: string;
  data: Array<{
    timestamp: string;
    value: number;
    details?: Record<string, any>;
  }>;
}

export interface PerformanceScorecardParams {
  organizationId: string;
  aggregationLevel: AggregationLevel;
  entityId?: string;
  timeWindow: TimeWindow;
  customStart?: string;
  customEnd?: string;
}

class PerformanceService {
  async getScorecard(params: PerformanceScorecardParams): Promise<PerformanceScorecard> {
    const queryParams = new URLSearchParams();
    queryParams.append('organizationId', params.organizationId);
    queryParams.append('aggregationLevel', params.aggregationLevel);
    if (params.entityId) queryParams.append('entityId', params.entityId);
    queryParams.append('timeWindow', params.timeWindow);
    if (params.customStart) queryParams.append('customStart', params.customStart);
    if (params.customEnd) queryParams.append('customEnd', params.customEnd);

    const response = await api.get(`/performance/scorecard?${queryParams.toString()}`);
    return response.data;
  }

  async getMetricDrilldown(
    organizationId: string,
    metricName: string,
    aggregationLevel: AggregationLevel,
    timeWindow: TimeWindow,
    entityId?: string,
    customStart?: string,
    customEnd?: string
  ): Promise<MetricDrilldown> {
    const queryParams = new URLSearchParams();
    queryParams.append('organizationId', organizationId);
    queryParams.append('metricName', metricName);
    queryParams.append('aggregationLevel', aggregationLevel);
    queryParams.append('timeWindow', timeWindow);
    if (entityId) queryParams.append('entityId', entityId);
    if (customStart) queryParams.append('customStart', customStart);
    if (customEnd) queryParams.append('customEnd', customEnd);

    const response = await api.get(`/performance/drilldown?${queryParams.toString()}`);
    return response.data;
  }

  async getAgentPerformanceRatings(
    organizationId: string,
    timeWindow: TimeWindow,
    teamId?: string,
    customStart?: string,
    customEnd?: string
  ): Promise<AgentPerformanceRating[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('organizationId', organizationId);
    queryParams.append('timeWindow', timeWindow);
    if (teamId) queryParams.append('teamId', teamId);
    if (customStart) queryParams.append('customStart', customStart);
    if (customEnd) queryParams.append('customEnd', customEnd);

    const response = await api.get(`/performance/agent-ratings?${queryParams.toString()}`);
    return response.data;
  }

  async updateMetricWeights(
    organizationId: string,
    weights: AgentPerformanceRating['metricWeights']
  ): Promise<void> {
    await api.post('/performance/metric-weights', {
      organizationId,
      weights,
    });
  }

  async updateAgentRanking(
    organizationId: string,
    rankings: Array<{ agentId: string; rank: number }>
  ): Promise<void> {
    await api.post('/performance/update-rankings', {
      organizationId,
      rankings,
    });
  }

  exportToCSV(scorecard: PerformanceScorecard): void {
    const rows: string[] = [];

    // Header
    rows.push('Performance Scorecard Report');
    rows.push(`Generated: ${new Date().toLocaleString()}`);
    rows.push(`Time Window: ${scorecard.timeWindow.label}`);
    rows.push(`Aggregation: ${scorecard.aggregationLevel}`);
    if (scorecard.entityName) {
      rows.push(`Entity: ${scorecard.entityName}`);
    }
    rows.push('');

    // Metrics header
    rows.push('Metric,Current,Previous,Delta (%),Trend');

    // WFM Adherence
    const wfm = scorecard.metrics.wfmAdherence;
    rows.push(`WFM Adherence,${wfm.current.toFixed(2)}${wfm.unit},${wfm.previous.toFixed(2)}${wfm.unit},${wfm.delta.toFixed(2)}%,${wfm.trend}`);

    // Occupancy
    const occ = scorecard.metrics.occupancy;
    rows.push(`Occupancy,${occ.current.toFixed(2)}${occ.unit},${occ.previous.toFixed(2)}${occ.unit},${occ.delta.toFixed(2)}%,${occ.trend}`);

    // Productivity
    const prod = scorecard.metrics.productivity;
    rows.push(`Productivity (Solves/Hour),${prod.current.toFixed(2)},${prod.previous.toFixed(2)},${prod.delta.toFixed(2)}%,${prod.trend}`);

    // CSAT
    const csat = scorecard.metrics.csat;
    rows.push(`CSAT Score,${csat.current.toFixed(2)},${csat.previous.toFixed(2)},${csat.delta.toFixed(2)}%,${csat.trend}`);

    // SLA
    const sla = scorecard.metrics.sla;
    rows.push(`SLA Compliance,${sla.current.toFixed(2)}${sla.unit},${sla.previous.toFixed(2)}${sla.unit},${sla.delta.toFixed(2)}%,${sla.trend}`);

    // Cost per Contact
    const cost = scorecard.metrics.costPerContact;
    rows.push(`Cost per Contact,${cost.unit}${cost.current.toFixed(2)},${cost.unit}${cost.previous.toFixed(2)},${cost.delta.toFixed(2)}%,${cost.trend}`);

    // QA Rating
    const qa = scorecard.metrics.qaRating;
    rows.push(`QA Rating,${qa.current.toFixed(2)},${qa.previous.toFixed(2)},${qa.delta.toFixed(2)}%,${qa.trend}`);

    // Create and download CSV
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `performance-scorecard-${Date.now()}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  exportAgentRatingsToCSV(ratings: AgentPerformanceRating[]): void {
    const rows: string[] = [];

    // Header
    rows.push('Agent Performance Ratings');
    rows.push(`Generated: ${new Date().toLocaleString()}`);
    rows.push('');

    // Column headers
    rows.push('Rank,Agent Name,Team,Overall Score,WFM Adherence,Occupancy,Productivity,CSAT,SLA,Cost/Contact,QA Rating');

    // Agent data
    ratings.forEach((rating) => {
      const scores = rating.metricScores;
      rows.push(
        `${rating.rank},${rating.agentName},${rating.teamName || 'N/A'},${rating.overallScore.toFixed(2)},` +
        `${scores.wfmAdherence.toFixed(2)},${scores.occupancy.toFixed(2)},${scores.productivity.toFixed(2)},` +
        `${scores.csat.toFixed(2)},${scores.sla.toFixed(2)},${scores.costPerContact.toFixed(2)},${scores.qaRating.toFixed(2)}`
      );
    });

    // Create and download CSV
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `agent-performance-ratings-${Date.now()}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export const performanceService = new PerformanceService();
